import React, { useState, useEffect } from 'react';
import { Card, Button, Typography, Space, Descriptions, Collapse, Tag, message, Spin } from 'antd';
import { ArrowLeftOutlined, DownOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { http } from '../utils/http';

const { Title, Text } = Typography;

const OutboundDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [orderInfo, setOrderInfo] = useState(null);
  const [details, setDetails] = useState([]);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (id && !initialized) {
      loadData();
      setInitialized(true);
    }
  }, [id, initialized]);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await http.get('/erp/api/OutstorageOrder/GetDetail', {
        params: { id }
      });

      if (response.code === 1) {
        const data = response.data;
        setOrderInfo(data);
        setDetails(data.details || []);
      } else {
        message.error(response.message || '获取出库详情失败');
      }
    } catch (error) {
      console.error('加载数据失败:', error);
      message.error('网络错误，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/outbound');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '-';
    return `¥${Number(price).toFixed(2)}`;
  };

  // 渲染二级明细（库存记录）
  const renderStockRecords = (stockRecords, detailId) => {
    if (!stockRecords || stockRecords.length === 0) {
      return (
        <div style={{ padding: '16px', textAlign: 'center', color: '#999' }}>
          暂无库存记录
        </div>
      );
    }

    const handleStockRecordClick = (record) => {
      const params = new URLSearchParams({
        outstorageOrderDetailId: detailId,
        stockRecordId: record.stockRecordId || record.id,
        batch: record.batch || '',
        wareHouseName: record.wareHouseName || '',
        areaName: record.areaName || '',
        shelfName: record.shelfName || ''
      });
      navigate(`/scan?${params.toString()}`);
    };

    return stockRecords.map((record, index) => (
      <Card
        key={index}
        size="small"
        style={{
          marginBottom: 8,
          borderLeft: '3px solid #1890ff',
          backgroundColor: '#fafafa',
          cursor: 'pointer',
          transition: 'all 0.3s ease'
        }}
        bodyStyle={{ padding: '12px 16px' }}
        hoverable
        onClick={() => handleStockRecordClick(record)}
      >
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Text strong style={{ color: '#1890ff' }}>
              出库数量: {record.quantity || 0}
            </Text>
            <Text strong style={{ color: 'green' }}>
              已扫数量: {record.boxCount || 0}
            </Text>
            <Text type="secondary">
              {formatDate(record.createdTime)}
            </Text>
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Text type="secondary">
              批号: {record.batch || '-'}
            </Text>
            <Text strong style={{ color: '#f5222d' }}>
              {formatPrice(record.price)}
            </Text>
          </div>
          
          <div>
            <Text type="secondary" style={{ fontSize: 13 }}>
              仓库: {record.wareHouseName || '-'} / 
              库区: {record.areaName || '-'} / 
              货架: {record.shelfName || '-'}
            </Text>
          </div>
        </Space>
      </Card>
    ));
  };

  // 渲染一级明细（商品详情）
  const renderDetailItem = (detail, index) => {
    const hasStockRecords = detail.stockRecords && detail.stockRecords.length > 0;
    
    return (
      <Card
        key={detail.id || index}
        style={{
          marginBottom: 16,
          borderRadius: 12,
          border: '1px solid #f0f0f0'
        }}
        bodyStyle={{ padding: '20px' }}
      >
        {/* 一级信息 */}
        <div style={{ marginBottom: hasStockRecords ? 16 : 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
            <Text strong style={{ fontSize: 16, color: '#262626' }}>
              {detail.productName || '-'}
            </Text>
            <div style={{ display: 'flex', gap: 8 }}>
              <Tag color={detail.outStoragedQuantity >= detail.quantity ? 'green' : 'orange'}>
                {detail.outStoragedQuantity >= detail.quantity ? '已完成' : '进行中'}
              </Tag>
            </div>
          </div>
          
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text type="secondary">货品编码: {detail.productCode || '-'}</Text>
              <Text type="secondary">备案项号: {detail.baNo || '-'}</Text>
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text type="secondary">SKU编码: {detail.skuCode || '-'}</Text>
              <Text type="secondary">HS: {detail.hsCode || '-'}</Text>
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text type="secondary">SKU属性: {detail.combine || '-'}</Text>
              <Text type="secondary">SKU规格: {detail.speci || '-'}</Text>
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
              <Space size={16}>
                <Text>
                  出库数量: <Text strong style={{ color: '#1890ff' }}>{detail.quantity || 0}</Text>
                </Text>
                <Text>
                  已出总数: <Text strong style={{ color: detail.outStoragedQuantity >= detail.quantity ? '#52c41a' : '#faad14' }}>
                    {detail.outStoragedQuantity || 0}
                  </Text>
                </Text>
              </Space>
            </div>
          </Space>
        </div>

        {/* 二级信息（库存记录） */}
        {hasStockRecords && (
          <Collapse
            ghost
            defaultActiveKey={['1']}
            expandIcon={({ isActive }) => <DownOutlined rotate={isActive ? 180 : 0} />}
            items={[
              {
                key: '1',
                label: (
                  <Text strong style={{ color: '#1890ff' }}>
                    出库记录 ({detail.stockRecords.length}条)
                  </Text>
                ),
                children: renderStockRecords(detail.stockRecords, detail.id)
              }
            ]}
          />
        )}
      </Card>
    );
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px 0' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text type="secondary">加载中...</Text>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* 页面头部 */}
      <div className="page-header">
        <Space>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={handleBack}
            type="text"
            size="large"
          />
          <Title level={3} style={{ margin: 0 }}>
            出库明细
          </Title>
        </Space>
      </div>

      {/* 页面内容 */}
      <div className="page-content" style={{ padding: '0 20px' }}>
        {/* 出库单基础信息 */}
        {orderInfo && (
          <Card 
            title="出库单信息"
            style={{ marginBottom: 24, borderRadius: 12 }}
            bodyStyle={{ padding: '20px 24px' }}
          >
            <Descriptions column={3} size="middle">
              <Descriptions.Item label="出库单号" span={3}>
                <Text strong style={{ fontSize: 16, color: '#1890ff' }}>
                  {orderInfo.code || '-'}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="客户名称">
                {orderInfo.customerName || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="客户出库单号">
                {orderInfo.customerOutstorageCode || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="客户出库日期">
                {orderInfo.customerOutstorageDate || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="报关单号">
                {orderInfo.declarationNo || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="核注清单编码">
                {orderInfo.entryNo || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="瑞圜入库跟单号">
                {orderInfo.ryOrderNo || '-'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        )}

        {/* 出库明细 */}
        <Card
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text strong style={{ fontSize: 16 }}>出库明细</Text>
              <Text type="secondary" style={{ fontSize: 14 }}>
                共 {details.length} 项商品
              </Text>
            </div>
          }
          style={{ borderRadius: 12 }}
          bodyStyle={{ padding: '20px' }}
        >
          {details.length > 0 ? (
            <div>
              {details.map((detail, index) => renderDetailItem(detail, index))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Text type="secondary">暂无出库明细</Text>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default OutboundDetail;